/**
 * Operator strategy analysis — the third scoring lane.
 *
 * A benchmark run is described by three complementary records:
 *  - OUTCOME  (ScenarioScore / benchmark results): what happened.
 *  - BEHAVIOR (ScenarioBehavior): how the operator played, mechanically.
 *  - STRATEGY (this file): why it played that way — an LLM judge's
 *    doctrine-level read of the operator's shift, generated from a
 *    STACK-BLIND digest of the run so the judge cannot grade a stack by
 *    reputation.
 *
 * Provenance: a judge-model change is a provenance change, so the judging
 * model config + model id are stamped on every analysis. `schemaVersion` is
 * additive-only.
 */
import { z } from 'zod';
/** One doctrine axis: a 1–5 rating with the evidence that justifies it. */
export const StrategyDoctrineAxisSchema = z.object({
    /** 1 = negligent · 3 = adequate · 5 = exemplary. */
    rating: z.number().int().min(1).max(5),
    /** Concrete evidence from the run log ("launched the 4T at t+64s, 56s before the thermal spawned"). */
    evidence: z.string().min(1).max(1000),
});
/** The doctrine axes every operator is graded on. */
export const StrategyDoctrineSchema = z.object({
    /** Severity/TTL triage quality — did the right events get attention first? */
    prioritization: StrategyDoctrineAxisSchema,
    /** Anticipation — positioning assets before demand (e.g. pre-staging the thermal drone). */
    preStaging: StrategyDoctrineAxisSchema,
    /** Fleet used as a fleet — concurrent tasking vs a one-robot queue. */
    parallelism: StrategyDoctrineAxisSchema,
    /** Response to failures mid-shift — retries, reroutes, abandoning lost causes. */
    recovery: StrategyDoctrineAxisSchema,
    /** Tool economy — wasted calls, blind spots, redundant polling, timeout hygiene. */
    toolDiscipline: StrategyDoctrineAxisSchema,
});
/**
 * A single next-step idea from the judge: one thing to try differently on
 * the NEXT attempt, grounded in what the log actually showed. Advisory only
 * — the judge suggests, a human (or a future coordinator) decides whether to
 * act on it. Deliberately free text, not a `RunProfile` diff: the judge has
 * no visibility into what knobs exist or which arm produced this run.
 */
export const StrategyExperimentSuggestionSchema = z.object({
    /** The one change to try next ("disable memory for this scenario"). */
    suggestion: z.string().min(1).max(300),
    /** Why — tied to specific evidence from THIS run, not generic advice. */
    rationale: z.string().min(1).max(500),
});
export const StrategyDecisionSchema = z.object({
    /** Mission-relative time of the decision, in seconds. */
    tSec: z.number().min(0),
    /** What the operator did (or conspicuously failed to do). */
    decision: z.string().min(1).max(500),
    /** The judge's assessment of that call. */
    assessment: z.string().min(1).max(500),
});
/**
 * The human-readable read of the operator's THINKING — distinct from
 * doctrine (how well it executed) and the narrative (the shift summary).
 * Written for the engineer reviewing a run: what did the operator reason
 * about and work out, where did its reasoning surprise us relative to what
 * the mission spec actually spelled out, where did its reasoning break
 * down, and what its own running observations show it learned and then
 * used. All four lists are concrete and evidence-tied; empty beats filler.
 */
export const StrategyReasoningReviewSchema = z.object({
    /** What the operator reasoned about and figured out during the shift — inferences, plans, connections it made. */
    figuredOut: z.array(z.string().min(1).max(500)).max(8).default([]),
    /** Reasoning that surprised the judge given the mission spec — connections or inferences the brief never spelled out (good or bad). */
    surprises: z.array(z.string().min(1).max(500)).max(6).default([]),
    /** What the operator did NOT reason well about — wrong inferences, missed connections, unexamined assumptions. */
    reasoningGaps: z.array(z.string().min(1).max(500)).max(8).default([]),
    /**
     * What the operator's OWN running observations show it learned about the
     * environment, and whether it then acted on that — distinct from
     * `figuredOut` (in-the-moment inference) because this is knowledge it
     * wrote down and could have reused.
     */
    memoryUse: z.array(z.string().min(1).max(500)).max(6).default([]),
});
/**
 * What the judge model itself produces (the model never emits provenance
 * metadata — that is stamped by the service that ran it).
 */
export const OperatorStrategyVerdictSchema = z.object({
    doctrine: StrategyDoctrineSchema,
    /** The pivotal moments of the shift, in mission order. */
    decisionTimeline: z.array(StrategyDecisionSchema).max(12).default([]),
    strengths: z.array(z.string().min(1).max(500)).max(8).default([]),
    weaknesses: z.array(z.string().min(1).max(500)).max(8).default([]),
    /** Root causes behind failed/expired events, when any exist. */
    failureRootCauses: z.array(z.string().min(1).max(500)).max(8).default([]),
    /**
     * The human read of the operator's reasoning (figured out / surprised us /
     * reasoned poorly). Defaulted so analyses persisted before the section
     * existed still parse — they render as an empty review.
     */
    reasoningReview: StrategyReasoningReviewSchema.default({
        figuredOut: [],
        surprises: [],
        reasoningGaps: [],
        memoryUse: [],
    }),
    /**
     * Up to 3 ideas for what to try on the next attempt, ranked most-promising
     * first. Optional — a clean shift with no weaknesses may have none. This is
     * a suggestion surface only; nothing consumes it automatically.
     */
    experimentSuggestions: z.array(StrategyExperimentSuggestionSchema).max(3).default([]),
    /** The shift-report paragraph a fleet-ops lead would write. */
    narrative: z.string().min(1).max(4000),
});
/** Size accounting for the digest the judge actually saw. */
export const StrategyDigestStatsSchema = z.object({
    events: z.number().int().nonnegative(),
    actions: z.number().int().nonnegative(),
    /** True when the digest was cut to fit the token cap — the judge saw a partial log. */
    truncated: z.boolean(),
    /**
     * True when a stack-anonymized COORDINATION STRUCTURE section (§10.6) was
     * folded into the digest — i.e. the judge could reason about the operator's
     * self-organization (parallelism/coordination), not just its robot commands.
     * Optional+default so analyses persisted before the linkage still parse.
     */
    topologyIncluded: z.boolean().default(false),
    /**
     * True when OPERATOR REASONING excerpts were folded into the digest —
     * i.e. the judge could see what the operator thought, not just what it
     * did. Essential for interpreting a report: a review written blind to
     * reasoning is a weaker artifact than one written with it. Optional+default
     * so analyses persisted before the field still parse.
     */
    reasoningIncluded: z.boolean().default(false),
    /**
     * True when the operator's own running notes were folded into the digest —
     * i.e. the judge could see what it wrote down for itself. Essential for
     * interpreting a report: a review written blind to notes is a weaker
     * artifact than one written with it. Optional+default so analyses
     * persisted before the field still parse.
     */
    notesIncluded: z.boolean().default(false),
});
/** The persisted record: verdict + judge provenance. */
export const OperatorStrategyAnalysisSchema = OperatorStrategyVerdictSchema.extend({
    schemaVersion: z.literal(1),
    judgeModelConfigId: z.string().min(1),
    /** Resolved model id at judging time (e.g. "claude-haiku-4-5-…"). */
    judgeModelId: z.string().min(1),
    generatedAt: z.string().datetime({ offset: true }),
    digestStats: StrategyDigestStatsSchema,
});
/**
 * Judge failures are recorded, never thrown into the run lifecycle: a run
 * whose judging failed carries this shape instead of an analysis.
 */
export const OperatorStrategyErrorSchema = z.object({
    schemaVersion: z.literal(1),
    error: z.string().min(1),
    judgeModelConfigId: z.string().min(1).optional(),
    generatedAt: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=operator-strategy.js.map